import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
    ExportFormat,
    ExportTheme,
    ExportConfig,
    ExportMode,
} from '../types/models';

/**
 * Zustand store for chat export and management UI state.
 *
 * This is a static singleton store that manages:
 * - Export configuration (format, theme, config)
 * - Chat selection state
 * - Preview content and loading state
 * - Delete modal UI state
 *
 * Platform-specific operations (file save, PDF generation) are NOT in this store.
 * Those are handled by IExportService, passed to handler functions at call time.
 *
 * @example Store usage
 * ```tsx
 * const { actions, helpers } = useChatManagementStore();
 * const exportState = useChatManagementStore((s) => s.export);
 *
 * // Update export format
 * actions.setExportFormat('pdf');
 *
 * // Check if export is possible
 * const canExport = helpers.canExport();
 *
 * // Reset on modal unmount
 * useEffect(() => () => actions.reset(), [actions]);
 * ```
 */

// ============= State Types =============

export interface ExportState {
    format: ExportFormat;
    theme: ExportTheme;
    mode: ExportMode;
    isExporting: boolean;
    progress: {
        current: number;
        total: number;
    };
    config: ExportConfig;
    encryptExport: boolean;
    signExport: boolean;
}

export interface ChatSelectionState {
    selectedChats: string[];
    selectedChat: string | null;
    selectedChatMessages: number | null;
    selectedChatImages: number | null;
    isLoadingMessages: boolean;
}

export interface PreviewState {
    content: string;
    isLoading: boolean;
    error: string;
}

export interface ChatManagementState {
    export: ExportState;
    selection: ChatSelectionState;
    preview: PreviewState;
    ui: {
        singleDelete: {
            open: boolean;
            chatId?: string;
            title?: string;
        };
        bulkDeleteOpen: boolean;
    };
}

// ============= Initial State =============

export const initialExportState: ExportState = {
    format: 'pdf',
    theme: 'dark',
    mode: null,
    isExporting: false,
    progress: { current: 0, total: 0 },
    config: {
        // Content options
        includeMarkdown: true,
        includeCitations: true,
        includeReasoning: true,
        includeMetadata: true,
        includeTimestamp: false,
        // Display options
        showModel: true,
        showTitle: true,
        showRoleHeader: true,
        // Layout options
        includeHeader: true,
        includeFooter: true,
        // Image options
        includeImages: true,
        embedImages: true,
        imageQuality: 100,
    },
    encryptExport: false,
    signExport: false,
};

export const initialSelectionState: ChatSelectionState = {
    selectedChats: [],
    selectedChat: null,
    selectedChatMessages: null,
    selectedChatImages: null,
    isLoadingMessages: false,
};

export const initialPreviewState: PreviewState = {
    content: '',
    isLoading: false,
    error: '',
};

const initialState: ChatManagementState = {
    export: initialExportState,
    selection: initialSelectionState,
    preview: initialPreviewState,
    ui: {
        singleDelete: { open: false },
        bulkDeleteOpen: false,
    },
};

// ============= Store Type =============

export type ChatManagementStore = ChatManagementState & {
    actions: {
        // Export actions
        setExportFormat: (format: ExportFormat) => void;
        setExportTheme: (theme: ExportTheme) => void;
        setExportConfig: (config: ExportConfig) => void;
        setExportMode: (mode: ExportMode) => void;
        setExportProgress: (current: number, total: number) => void;
        setIsExporting: (isExporting: boolean) => void;
        setEncryptExport: (encrypt: boolean) => void;
        setSignExport: (sign: boolean) => void;
        initializeExportState: (state: Partial<ExportState>) => void;
        resetExportState: () => void;

        // Selection actions
        // TODO : Ensure this mingles with future ChatStore actions / state
        setSelectedChats: (chats: string[]) => void;
        setSelectedChat: (chatId: string | null) => void;
        setChatMessages: (count: number | null) => void;
        setChatImages: (count: number | null) => void;
        setIsLoadingMessages: (isLoading: boolean) => void;
        toggleChatSelection: (chatId: string) => void;
        resetSelectionState: () => void;

        // Preview actions
        setPreviewContent: (content: string) => void;
        setPreviewError: (error: string) => void;
        setPreviewLoading: (isLoading: boolean) => void;
        resetPreviewState: () => void;

        // UI actions
        setSingleDelete: (open: boolean, chatId?: string, title?: string) => void;
        setBulkDeleteOpen: (open: boolean) => void;

        // Full reset - call on modal unmount
        // TODO : validate necessity of this
        reset: () => void;
    };
    helpers: {
        getCurrentMode: () => ExportMode;
        canExport: () => boolean;
        areAllSelected: (totalChats: number) => boolean;
        hasSelectedChatData: () => boolean;
    };
};

const enableDevtools = process.env.NODE_ENV !== 'production';

/**
 * Static singleton store for chat export UI state.
 */
export const useChatManagementStore = create<ChatManagementStore>()(
    devtools(
        (set, get) => ({
            ...initialState,
            actions: {
                // Export actions
                setExportFormat: (format) =>
                    set(
                        (state) => ({ export: { ...state.export, format } }),
                        false,
                        'export/setFormat',
                    ),
                setExportTheme: (theme) =>
                    set(
                        (state) => ({ export: { ...state.export, theme } }),
                        false,
                        'export/setTheme',
                    ),
                setExportConfig: (config) =>
                    set(
                        (state) => ({ export: { ...state.export, config } }),
                        false,
                        'export/setConfig',
                    ),
                setExportMode: (mode) =>
                    set(
                        (state) => ({ export: { ...state.export, mode } }),
                        false,
                        'export/setMode',
                    ),
                setExportProgress: (current, total) =>
                    set(
                        (state) => ({
                            export: {
                                ...state.export,
                                progress: { current, total },
                            },
                        }),
                        false,
                        'export/setProgress',
                    ),
                setIsExporting: (isExporting) =>
                    set(
                        (state) => ({
                            export: { ...state.export, isExporting },
                        }),
                        false,
                        'export/setIsExporting',
                    ),
                setEncryptExport: (encrypt) =>
                    set(
                        (state) => ({
                            export: { ...state.export, encryptExport: encrypt },
                        }),
                        false,
                        'export/setEncryptExport',
                    ),
                setSignExport: (sign) =>
                    set(
                        (state) => ({
                            export: { ...state.export, signExport: sign },
                        }),
                        false,
                        'export/setSignExport',
                    ),
                initializeExportState: (partialState) =>
                    set(
                        (state) => ({
                            export: { ...state.export, ...partialState },
                        }),
                        false,
                        'export/initialize',
                    ),
                resetExportState: () =>
                    set(
                        () => ({ export: initialExportState }),
                        false,
                        'export/reset',
                    ),

                // Selection actions
                setSelectedChats: (chats) =>
                    set(
                        (state) => ({
                            selection: {
                                ...state.selection,
                                selectedChats: chats,
                            },
                        }),
                        false,
                        'selection/setSelectedChats',
                    ),
                setSelectedChat: (chatId) =>
                    set(
                        (state) => ({
                            selection: { ...state.selection, selectedChat: chatId },
                        }),
                        false,
                        'selection/setSelectedChat',
                    ),
                setChatMessages: (count) =>
                    set(
                        (state) => ({
                            selection: {
                                ...state.selection,
                                selectedChatMessages: count,
                            },
                        }),
                        false,
                        'selection/setChatMessages',
                    ),
                setChatImages: (count) =>
                    set(
                        (state) => ({
                            selection: {
                                ...state.selection,
                                selectedChatImages: count,
                            },
                        }),
                        false,
                        'selection/setChatImages',
                    ),
                setIsLoadingMessages: (isLoading) =>
                    set(
                        (state) => ({
                            selection: {
                                ...state.selection,
                                isLoadingMessages: isLoading,
                            },
                        }),
                        false,
                        'selection/setIsLoading',
                    ),
                toggleChatSelection: (chatId) => {
                    const state = get();
                    const isSelected =
                        state.selection.selectedChats.includes(chatId);
                    const newSelectedChats = isSelected
                        ? state.selection.selectedChats.filter(
                              (id) => id !== chatId,
                          )
                        : [...state.selection.selectedChats, chatId];

                    set(
                        (s) => ({
                            selection: {
                                ...s.selection,
                                selectedChats: newSelectedChats,
                            },
                        }),
                        false,
                        'selection/toggleChat',
                    );
                },
                resetSelectionState: () =>
                    set(
                        () => ({ selection: initialSelectionState }),
                        false,
                        'selection/reset',
                    ),

                // Preview actions
                setPreviewContent: (content) =>
                    set(
                        (state) => ({
                            preview: {
                                ...state.preview,
                                content,
                                error: '',
                            },
                        }),
                        false,
                        'preview/setContent',
                    ),
                setPreviewError: (error) =>
                    set(
                        (state) => ({
                            preview: {
                                ...state.preview,
                                error,
                                content: '',
                            },
                        }),
                        false,
                        'preview/setError',
                    ),
                setPreviewLoading: (isLoading) =>
                    set(
                        (state) => ({
                            preview: { ...state.preview, isLoading },
                        }),
                        false,
                        'preview/setLoading',
                    ),
                resetPreviewState: () =>
                    set(
                        () => ({ preview: initialPreviewState }),
                        false,
                        'preview/reset',
                    ),

                // UI actions
                setSingleDelete: (open, chatId, title) =>
                    set(
                        (state) => ({
                            ui: {
                                ...state.ui,
                                singleDelete: { open, chatId, title },
                            },
                        }),
                        false,
                        'ui/setSingleDelete',
                    ),
                setBulkDeleteOpen: (open) =>
                    set(
                        (state) => ({
                            ui: { ...state.ui, bulkDeleteOpen: open },
                        }),
                        false,
                        'ui/setBulkDeleteOpen',
                    ),

                // Full reset - call on modal unmount to clear transient state
                reset: () => set(initialState, false, 'reset'),
            },
            helpers: {
                getCurrentMode: () => {
                    const state = get();
                    if (state.export.mode) return state.export.mode;
                    return null; // Default to details view (null mode)
                },
                canExport: () => {
                    const state = get();
                    return (
                        state.selection.selectedChats.length > 0 &&
                        !state.export.isExporting
                    );
                },
                areAllSelected: (totalChats) => {
                    const state = get();
                    return (
                        state.selection.selectedChats.length === totalChats &&
                        totalChats > 0
                    );
                },
                hasSelectedChatData: () => {
                    const state = get();
                    return state.selection.selectedChat !== null;
                },
            },
        }),
        { enabled: enableDevtools, name: 'chat-management-store' },
    ),
);
